import React from "react";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import { useMassFormatter } from "hooks/useMassFormatter";
import {
  HeatMaterialWithColor,
  MixValueMode,
} from "components/strategyDashboard/heats/types";
import { HeatMixDiffPill } from "./HeatMixDiffPill";
import { useNumberSerialiser } from "hooks/serialisers/numbers";
import { useUnitsFormatter } from "hooks/formatters";
import { useMaterialDisplayName } from "hooks/useMaterials";
import { useTenantData } from "contexts/tenant";

interface HeatMixPlotLayerProps {
  material: HeatMaterialWithColor;
  height: number;
  isFirst: boolean;
  mixValueMode?: MixValueMode;
}

export const HeatMixPlotLayer: React.FC<HeatMixPlotLayerProps> = ({
  material,
  height,
  isFirst,
  mixValueMode = "mass",
}) => {
  const theme = useTheme();
  const { formatMass } = useMassFormatter();
  const { small_mass_per_mass: smallMassPerMass } = useTenantData();
  const unitsFormatter = useUnitsFormatter(false);
  const { format: formatNumber } = useNumberSerialiser({ decimalPlaces: 1 });
  const { format: formatSmallMass } = useNumberSerialiser({ decimalPlaces: 0 });
  const materialDisplayName = useMaterialDisplayName();

  const format =
    mixValueMode === "mass_fraction"
      ? (value: number) => `${formatNumber(value)}%`
      : mixValueMode === "small_mass"
        ? (value: number) =>
            `${formatSmallMass(value * smallMassPerMass)}${unitsFormatter("small_mass")}`
        : material.unit == "mass"
          ? formatMass
          : material.unit == "grab"
            ? (value: number) => `+${formatNumber(value)}`
            : formatNumber;

  const formatDiff =
    mixValueMode === "mass_fraction"
      ? (value: number) => `${formatNumber(value)}%`
      : mixValueMode === "small_mass"
        ? (value: number) =>
            `${formatSmallMass(value * smallMassPerMass)}${unitsFormatter("small_mass")}`
        : material.unit == "mass"
          ? formatMass
          : formatNumber;

  const isInactive = material.value === 0;
  const displayName = materialDisplayName(material.name);

  return (
    <Box
      sx={{
        border: `1px solid ${theme.palette.grey[800]}`,
        minHeight: height,
        display: "flex",
        zIndex: 1,
        opacity: isInactive || material.ignored ? 0.4 : 1,
        backgroundColor:
          isInactive || material.ignored
            ? theme.palette.grey[100]
            : "transparent",
      }}
      px={3}
      title={displayName}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        width="100%"
      >
        <Box
          sx={{
            backgroundColor: material.color,
            width: 10,
            height: 10,
            borderRadius: "50%",
          }}
        />
        <Typography
          sx={{
            fontWeight: material.highlighted
              ? "fontWeightBold"
              : "fontWeightMedium",
            lineHeight: 1.3,
            flex: 1,
            minWidth: 0,
            wordBreak: "break-word",
          }}
        >
          {displayName}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontWeight: material.highlighted
              ? "fontWeightBold"
              : "fontWeightMedium",
          }}
        >
          {format(material.value)}
        </Typography>
        {isFirst ? null : (
          <HeatMixDiffPill
            diffFromPreviousStage={material.diffFromPreviousStage}
            format={formatDiff}
            highlighted={material.highlighted}
          />
        )}
      </Stack>
    </Box>
  );
};
